/**
 * Flight Query Hooks
 * React Query hooks for fetching flight data
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { flightService } from '@/services/flightService';
import type { CreateFlightLogRequest, FlightStatus } from '@/types/flight';

/**
 * Query key factory for flights
 */
export const flightKeys = {
  all: ['flights'] as const,
  lists: () => [...flightKeys.all, 'list'] as const,
  list: (userId?: number) => [...flightKeys.lists(), { userId }] as const,
  details: () => [...flightKeys.all, 'detail'] as const,
  detail: (id: number) => [...flightKeys.details(), id] as const,
  summaries: () => [...flightKeys.all, 'summary'] as const,
  summary: (userId: number, year: number, month: number) =>
    [...flightKeys.summaries(), { userId, year, month }] as const,
  pay: (userId: number, year?: number, month?: number) =>
    [...flightKeys.all, 'pay', { userId, year, month }] as const,
};

/**
 * Fetch all flights (optionally filtered by user)
 */
export function useFlights(userId?: number) {
  return useQuery({
    queryKey: flightKeys.list(userId),
    queryFn: () => flightService.getFlights(userId),
  });
}

/**
 * Fetch a single flight by ID
 */
export function useFlight(id: number) {
  return useQuery({
    queryKey: flightKeys.detail(id),
    queryFn: () => flightService.getFlight(id),
    enabled: !!id,
  });
}

/**
 * Fetch monthly summary for a user
 */
export function useMonthlySummary(userId: number, year: number, month: number) {
  return useQuery({
    queryKey: flightKeys.summary(userId, year, month),
    queryFn: () => flightService.getMonthlySummary(userId, year, month),
    enabled: !!userId && !!year && !!month,
  });
}

/**
 * Fetch pay calculation for a user
 */
export function usePayCalculation(userId: number, year?: number, month?: number) {
  return useQuery({
    queryKey: flightKeys.pay(userId, year, month),
    queryFn: () => flightService.getPayCalculation(userId, year, month),
    enabled: !!userId,
  });
}

/**
 * Create a new flight (mutation) - for legacy FlightLog entries
 */
export function useCreateFlight() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateFlightLogRequest) => flightService.createFlight(data),
    onSuccess: () => {
      // Invalidate and refetch flights list
      queryClient.invalidateQueries({ queryKey: flightKeys.lists() });
      queryClient.invalidateQueries({ queryKey: flightKeys.summaries() });
    },
  });
}

/**
 * Update a flight (mutation) - for legacy FlightLog entries
 */
export function useUpdateFlight() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CreateFlightLogRequest> }) =>
      flightService.updateFlight(id, data),
    onSuccess: (_, variables) => {
      // Invalidate specific flight and lists
      queryClient.invalidateQueries({ queryKey: flightKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: flightKeys.lists() });
      queryClient.invalidateQueries({ queryKey: flightKeys.summaries() });
    },
  });
}

/**
 * Update flight status (mutation) - for Trip-based Flight status transitions
 */
export function useUpdateFlightStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ flightId, status }: { flightId: number; status: FlightStatus }) =>
      flightService.updateFlightStatus(flightId, status),
    onSuccess: () => {
      // Invalidate trips and flights
      queryClient.invalidateQueries({ queryKey: ['trips'] });
      queryClient.invalidateQueries({ queryKey: flightKeys.lists() });
    },
  });
}

/**
 * Delete a flight (mutation)
 */
export function useDeleteFlight() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => flightService.deleteFlight(id),
    onSuccess: () => {
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: flightKeys.lists() });
      queryClient.invalidateQueries({ queryKey: flightKeys.summaries() });
    },
  });
}
